<template>
  <div class="fts-device-preview">
    <template v-if="isReady">
      <div class="device-header">
        <div class="device-header_l">
          <div class="device-header-logo-wrapper">
            <img :src="appLogo" class="device-header-logo" />
          </div>
          <div class="device-header-name">Inistate</div>
        </div>

        <div class="device-header_c">
          <fts-tippy
            content="Mobile preview"
            class="device-header-action"
            :class="{'device-header-action--active': device == PhonePortrait}"
            @trigger:click="setDevice(PhonePortrait)"
          >
            <font-awesome-icon :icon="['fas', 'mobile-screen-button']" fixed-width />
          </fts-tippy>

          <fts-tippy
            content="Tablet preview"
            class="device-header-action"
            :class="{'device-header-action--active': device == PadPortrait}"
            @trigger:click="setDevice(PadPortrait)"
          >
            <font-awesome-icon :icon="['fas', 'tablet-screen-button']" fixed-width />
          </fts-tippy>

          <fts-tippy
            content="Tablet preview (landscape)"
            class="device-header-action"
            :class="{'device-header-action--active': device == PadLandscape}"
            @trigger:click="setDevice(PadLandscape)"
          >
            <font-awesome-icon :icon="['fas', 'tablet-screen-button']" fixed-width :rotation="270" />
          </fts-tippy>

          <fts-tippy
            content="Desktop preview"
            class="device-header-action"
            :class="{'device-header-action--active': device == Web}"
            @trigger:click="setDevice(Web)"
          >
            <font-awesome-icon :icon="['fas', 'desktop']" fixed-width />
          </fts-tippy>
        </div>

        <div class="device-header_r">
          <fts-tippy
            content="Reload device"
            class="device-header-action"
          >
            <fts-btn
              icon
              text
              @click="reloadDevice"
            >
              <font-awesome-icon :icon="['fas', 'rotate']" fixed-width />
            </fts-btn>
          </fts-tippy>
        </div>
      </div>

      <div class="device-control" v-if="isAdminUser">
        <fts-run-as
          :workspace-id="workspaceId"
          :workspace-modules="workspaceModules"
          :users="workspaceUsers"
          :run-as.sync="isRunAs"
          @selected="setDeviceRunAs"
        />
      </div>

      <div class="device-content">
        <fts-device
          :device="device"
          :web-url="deviceWebUrl"
          :app-url="deviceAppUrl"
          ref="fts-device"
        />
      </div>
    </template>

    <fts-spinner
      full-screen
      variant="rive"
      v-else
    />
  </div>
</template>

<script>
import Vue from 'vue';
import VueCookies from 'vue-cookies';
Vue.use(VueCookies);

import LocalStorageService from '../store/LocalStorageService';
import webAppConfig from '../commons/webApp.config';
import { authorizationStore } from '../store/authorizationStore';
import { devicePreviewStore } from '../store/devicePreviewStore';
import commons from '../commons/commons';
import { DeviceKey, appHomeEntryQuery, appUrlForDashboard, buildDeviceUrls, entryKeyOf, forwardTargetOf, readWorkspaceChange, sameWorkspace, webUrlForAppHome } from './devicePreviewSync';

import FTSBtn from '../elements/FTSBtn';
import FTSDevice from './FTSDevice';
import FTSRunAs from '../elements/FTSRunAs';
import FTSSpinner from '../elements/FTSSpinner.vue';
import FTSTippy from '../elements/FTSTippy';

export default {
  components: {
    'fts-btn': FTSBtn,
    'fts-device': FTSDevice,
    'fts-run-as': FTSRunAs,
    'fts-spinner': FTSSpinner,
    'fts-tippy': FTSTippy
  },
  data: () => ({
    isReady: false,
    baseLocationOrigin: null,
    device: null,
    deviceWebUrl: null,
    deviceAppUrl: null,
    deviceWebEventMessage: null,
    deviceAppEventMessage: null,
    currentWsId: null,
    deviceWorkspaceIds: { web: null, app: null },
    lastEntrySentTo: { web: null, app: null },
    isRunAs: false
  }),
  methods: {
    setDevice(device) {
      this.device = device;
      this.syncDeviceWorkspace(this.deviceKeyOf(device));
    },
    deviceKeyOf(device) {
      return device === this.Web ? DeviceKey.WEB : DeviceKey.APP;
    },
    /* SS06256: both iframes stay mounted and a device switch only toggles v-show, so a device that
       is behind the workspace last chosen in either device is re-pointed at it when it is shown. */
    syncDeviceWorkspace(deviceKey) {
      if (!this.isReady || this.currentWsId === null || this.currentWsId === undefined) return;
      if (sameWorkspace(this.deviceWorkspaceIds[deviceKey], this.currentWsId)) return;

      const urls = buildDeviceUrls({
        webBaseUrl: webAppConfig.previewWebURL,
        appBaseUrl: webAppConfig.previewAppURL,
        accessToken: LocalStorageService.getAccessToken(),
        refreshToken: LocalStorageService.getRefreshToken(),
        wsId: this.currentWsId,
        origin: webAppConfig.baseURL
      });
      if (deviceKey === DeviceKey.WEB) {
        this.deviceWebUrl = urls[DeviceKey.WEB];
      } else {
        this.deviceAppUrl = urls[DeviceKey.APP];
      }
      this.deviceWorkspaceIds = { ...this.deviceWorkspaceIds, [deviceKey]: this.currentWsId };
    },
    onDeviceWorkspaceChanged({ device, wsId }) {
      this.deviceWorkspaceIds = { ...this.deviceWorkspaceIds, [device]: wsId };
      if (sameWorkspace(wsId, this.currentWsId)) return;

      this.currentWsId = wsId;
      // keeps workspaceModules (used for expandListing) on the workspace the devices now show
      devicePreviewStore.dispatch("loadWorkspace", { id: wsId }).catch(err => console.error(err));
    },
    getDeviceEventMessage(evt) {
      if (
        evt.data &&
        (typeof evt.data === 'string' || evt.data instanceof String) &&
        evt.data.indexOf('inistate') > -1
      ) {
        try {
          const res = JSON.parse(evt.data);
          console.info(`From Event ${res.device || '(undefined)'} to Builder`, res);

          const workspaceChange = readWorkspaceChange(res);

          if (workspaceChange) {
            this.onDeviceWorkspaceChanged(workspaceChange);
          } else if (res.onload) {
            console.info(`Device ${res.device} ready.`, new Date());
          } else if (res.url) {
            const routerResolved = this.$router.resolve(res.url.substring(res.url.indexOf('#') + 1));
            const route = routerResolved && routerResolved.route;

            // a device reporting the entry it was just sent is echoing it (SS06260) - absorbed once
            const entryKey = entryKeyOf(route);
            const lastEntrySentTo = this.lastEntrySentTo || {};
            if (entryKey && lastEntrySentTo[res.device] === entryKey) {
              this.lastEntrySentTo = { ...lastEntrySentTo, [res.device]: null };
            } else if (route) {
              this.deviceAppEventMessage = { url: res.url };
              this.deviceWebEventMessage = { url: `${this.baseLocationOrigin}/#${route.fullPath}` };

              // a form open over Mobile's Home shows over Desktop's dashboard (SS06260)
              const appHomeEntry = res.device === DeviceKey.APP ? appHomeEntryQuery(res.url) : null;
              if (appHomeEntry) {
                this.deviceWebEventMessage = { url: webUrlForAppHome(this.baseLocationOrigin, appHomeEntry) };
              }

              // only a module opened on its own expands the sidebar; an entry or activity navigates (SS06260)
              if (route.name === "list-view" && !route.query.l && !route.query.id && !route.query.a && route.params.moduleId) {
                const moduleId = commons.decodeids(route.params.moduleId);
                const module = this.workspaceModules.find(x => x.id == moduleId);

                if (module && module.menus && module.menus.length) {
                  this.deviceWebEventMessage = {
                    url: `${this.baseLocationOrigin}/#${route.fullPath}`, 
                    redirect: false, 
                    action: "expandListing", 
                    moduleId: moduleId
                  }
                }
              }

              // the app has no dashboard route: an entry open on the dashboard goes over its home (SS06257)
              if (this.deviceAppEventMessage.url && this.deviceAppEventMessage.url.includes('/dashboard')) {
                this.deviceAppEventMessage = { url: appUrlForDashboard(this.baseLocationOrigin, route.query) };
              }

              this.deviceAppEventMessage.url = this.getDeviceAppUrl();
              this.deviceWebEventMessage.url = this.getDeviceWebUrl();

              // a device still on the previous workspace reloads into the new one when shown
              const target = forwardTargetOf(res.device, this.deviceKeyOf(this.device));
              if (sameWorkspace(this.deviceWorkspaceIds[target], this.currentWsId)) {
                this.$refs["fts-device"].postMessage(
                  target === DeviceKey.APP ? { app: this.deviceAppEventMessage } : { web: this.deviceWebEventMessage }
                );
                // the sender moved on, so whatever it was last sent can no longer be echoed
                this.lastEntrySentTo = { ...lastEntrySentTo, [res.device]: null, [target]: entryKey };
              }
            } else {
              this.$toasted.error("getDeviceEventMessage; failed to get route");
            }
          }
        } catch(err) {
          console.error(err);
        } 
      }
    },
    sanitizeDeviceUrl(url) {
      const paramString = url.split('?')[1];
      const searchParams = new URLSearchParams(paramString);
      searchParams.delete("wsId");
      searchParams.delete("accessToken");
      searchParams.delete("refreshToken");
      searchParams.delete("builder");
      searchParams.delete("origin");
      searchParams.delete("refresh");
      searchParams.delete("runas");
      return `${url.split('?')[0]}${Boolean(searchParams.toString()) ? `?${searchParams.toString()}` : ''}`;
    },
    getDeviceWebUrl() {
      let url = (this.deviceWebEventMessage && this.deviceWebEventMessage.url) || `${this.baseLocationOrigin}/#/dashboard`;
      return this.sanitizeDeviceUrl(url);
    },
    getDeviceAppUrl() {
      let url = (this.deviceAppEventMessage && this.deviceAppEventMessage.url) || `${this.baseLocationOrigin}`;
      return this.sanitizeDeviceUrl(url);
    },
    setDeviceRunAs(payload) {
      const runAsTarget = `runas=${payload && payload.target ? payload.target : ""}`;
      let webUrl = this.getDeviceWebUrl();
      webUrl += webUrl.includes("?") ? `&${runAsTarget}` : `?${runAsTarget}`;
      let appUrl = this.getDeviceAppUrl();
      appUrl += appUrl.includes("?") ? `&${runAsTarget}` : `?${runAsTarget}`;

      this.$refs["fts-device"].postMessage({
        web: { url: webUrl },
        app: { url: appUrl }
      });
    },
    reloadDevice() {
      this.isRunAs = false;
      this.$refs["fts-device"].reload();
    }
  },
  computed: {
    isBeta() {
      return authorizationStore.getters.isBeta;
    },
    isAdminUser() {
      return authorizationStore.getters.isAdminUser;
    },
    appLogo() {
      return authorizationStore.getters.appLogo;
    },
    workspaceId() {
      return devicePreviewStore.getters.workspaceId;
    },
    workspaceModules() {
      return devicePreviewStore.getters.workspaceModules;
    },
    workspaceUsers() {
      return devicePreviewStore.getters.workspaceUsers;
    }
  },
  created() {
    window.appName = "inistate-builder";
    this.Web = "web";
    this.PhonePortrait = "phonePortrait";
    this.PhoneLandscape = "phoneLandscape";
    this.PadPortrait = "padPortrait";
    this.PadLandscape = "padLandscape";
  },
  async mounted() {
    await authorizationStore.dispatch("profile");
    await webAppConfig.getConfig();
    const workspaceId = $cookies.get('wsId');
    await devicePreviewStore.dispatch("loadWorkspace", { id: workspaceId });

    // Admin roles are per workspace (a Workspace Admin has none on the profile): check the roles of
    // the workspace being previewed, as the client sets them when it loads a workspace.
    const workspace = devicePreviewStore.state.workspace;
    authorizationStore.dispatch('setRoles', workspace && workspace.roles);
    if (!authorizationStore.getters.isAdminUser) {
      this.$toasted.error('401 Unauthorized access to Preview');
      this.$router.replace({ name: 'dashboard' });
      throw new Error("Unauthorized access to device preview. Redirect to dashboard.");
    }

    this.isReady = true;
    this.setDevice("phonePortrait");

    const urls = buildDeviceUrls({
      webBaseUrl: webAppConfig.previewWebURL,
      appBaseUrl: webAppConfig.previewAppURL,
      accessToken: LocalStorageService.getAccessToken(),
      refreshToken: LocalStorageService.getRefreshToken(),
      wsId: workspaceId,
      origin: webAppConfig.baseURL
    });
    this.baseLocationOrigin = webAppConfig.baseLocationOrigin;
    this.deviceWebUrl = urls[DeviceKey.WEB];
    this.deviceAppUrl = urls[DeviceKey.APP];
    this.currentWsId = workspaceId;
    this.deviceWorkspaceIds = { [DeviceKey.WEB]: workspaceId, [DeviceKey.APP]: workspaceId };

    window.addEventListener("message", this.getDeviceEventMessage);
  },
  beforeDestroy() {
    window.removeEventListener("message", this.getDeviceEventMessage);
  }
}
</script>

<style lang="scss">
.fts-device-preview {
  height: 100%;
  display: flex;
  flex-direction: column;
  background-color: var(--fts-background);
  color: var(--fts-text);
  .device-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 0.5rem 1rem;
    border-bottom: 1px solid var(--fts-border);
    background-color: var(--fts-card-background);
  }
  .device-header_l,
  .device-header_r {
    min-width: 125px;
  }
  .device-header_l {
    display: flex;
    align-items: center;
  }
  .device-header_c {
    display: flex;
    align-items: center;
    justify-content: center;
  }
  .device-header_r {
    display: flex;
    align-items: center;
    justify-content: flex-end;
  }
  .device-header-logo-wrapper {
    max-width: 24px;
    border-radius: 0.25rem;;
    background-color: var(--fts-card-background);
  }
  .device-header-logo {
    max-height: 100%;
    max-width: 100%;
    vertical-align: middle;
  }
  .device-header-name {
    margin-left: 0.375rem;
    font-weight: 500;
    font-size: 1.25rem;
  }
  .device-header-action {
    margin-left: 0.5rem;
    margin-right: 0.5rem;
    font-size: 1.25rem;
    color: var(--fts-text);
    cursor: pointer;
    &.device-header-action--active {
      color: var(--fts-primary);
    }
  }
  .device-control {
    padding-top: 0.5rem;
  }
  .device-content {
    flex: 1;
    height: 100%;
    display: flex;
    flex-direction: column;
    padding: 0.5rem 1rem;
    overflow: hidden;
    > * {
      flex: 1;
    }
  }
}
</style>